import { Component, effect, input, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ICampaignForm } from '@campaign-form/campaign-form.interface';
import { CampaignFinanceComponent } from '@campaign-form/form-fields/campaign-basics/campaign-finance/campaign-finance.component';
import { CampaignNotesComponent } from '@campaign-form/form-fields/campaign-basics/campaign-notes/campaign-notes.component';
import { ITeam } from '@core/interfaces/team.interface';
import { IObjectPermission } from '@core/interfaces/user.interface';
import { ECurrency } from '@enums/campaign.enum';
import { TranslateModule } from '@ngx-translate/core';
import { ICustomer } from '@pages/customers/customers.interface';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { IBriefStepState } from '../brief-step.interface';
import { IBriefBasicsFieldValue, TBriefBasicsFieldKind } from './basics-field-step.interface';

export type { IBriefBasicsFieldValue, TBriefBasicsFieldKind } from './basics-field-step.interface';

// campaign-notes.component.ts's own notes control caps input at 4096 chars (see
// CampaignNotesComponent) - this step reuses that component for its UI but not its FormGroup, so
// the cap has to be re-applied on this step's own control below or a brief with an over-length
// note would report this stop as valid, then have /brief/validate re-reject it right after Next.
const NOTES_MAX_LENGTH = 4096;

@Component({
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatErrorComponent,
    TranslateModule,
    CampaignFinanceComponent,
    CampaignNotesComponent,
  ],
  selector: 'app-brief-basics-field-step',
  templateUrl: './basics-field-step.component.html',
})
export class BriefBasicsFieldStepComponent {
  public readonly kind = input.required<TBriefBasicsFieldKind>();
  public readonly initialValue = input.required<IBriefBasicsFieldValue>();
  public readonly stateChange = output<IBriefStepState<IBriefBasicsFieldValue>>();

  // Shaped exactly like the real campaign-form's own form group so CampaignFinanceComponent /
  // CampaignNotesComponent can be reused verbatim instead of re-implementing their fields here -
  // each only reads its own control(s) (budget/currency, notes); every other control stays an
  // inert placeholder that's never bound to any UI in this step.
  public readonly form = new FormGroup<ICampaignForm>({
    budget: new FormControl<string | null>(null),
    currency: new FormControl<ECurrency | null>(null),
    customer: new FormControl<ICustomer | null>(null),
    customerId: new FormControl<string | null>(null),
    endDate: new FormControl<Date | null>(null),
    endTime: new FormControl<Date | null>(null),
    name: new FormControl<string | null>(null),
    notes: new FormControl<string | null>(null),
    startDate: new FormControl<Date | null>(null),
    startTime: new FormControl<Date | null>(null),
    team: new FormControl<ITeam | null>(null),
    teamId: new FormControl<string | null>(null),
    whitelist: new FormControl<IObjectPermission[] | null>([]),
  });

  constructor() {
    effect(() => {
      const kind = this.kind();

      this.form.controls.name.setValidators(kind === 'basicsName' ? Validators.required : null);
      this.form.controls.budget.setValidators(kind === 'basicsBudget' ? Validators.required : null);
      this.form.controls.currency.setValidators(kind === 'basicsBudget' ? Validators.required : null);
      this.form.controls.notes.setValidators(kind === 'basicsNotes' ? Validators.maxLength(NOTES_MAX_LENGTH) : null);

      const value = this.initialValue();

      this.form.patchValue(
        {
          budget: value.budget !== null ? String(value.budget) : null,
          currency: value.currency,
          name: value.name,
          notes: value.notes,
        },
        { emitEvent: false },
      );
      this.form.updateValueAndValidity({ emitEvent: false });
      this.form.markAllAsTouched();
      this.emitState();
    });

    this.form.valueChanges.subscribe(() => this.emitState());
  }

  private emitState(): void {
    const raw = this.form.getRawValue();

    this.stateChange.emit({
      valid: this.form.valid,
      value: {
        budget: raw.budget !== null && raw.budget !== '' ? Number(raw.budget) : null,
        currency: raw.currency,
        name: raw.name,
        notes: raw.notes,
      },
    });
  }
}
